import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Home, Car, Handshake, Star } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { Recensioni } from "../components/Recensioni";
import { Contatti } from "../components/Contatti";
import { ConnexaHomeSection } from "../components/ConnexaHomeSection";
import { ConnexaAutoSection } from "../components/ConnexaAutoSection";
import { ASSETS, IMAGES, HOME_SERVICES, AUTO_SERVICES, PROCESS, VALUES } from "../lib/data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.16]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-screen overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <picture>
          <source media="(max-width: 767px)" srcSet={IMAGES.heroCinematicMobile} />
          <img src={IMAGES.heroCinematic} alt="Connexa — coppia elegante, villa moderna e auto premium" className="h-full w-full object-cover img-cinematic" />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex h-full items-center">
        <div className="cx-container w-full">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }} className="overline"
          >
            Connexa — Consulenza su misura
          </motion.p>
          <h1 className="mt-6 font-display text-6xl font-light leading-[0.92] tracking-tight text-[var(--cx-white)] sm:text-7xl lg:text-[7.5rem]">
            {["Tu desideri.", "Noi realizziamo."].map((line, i) => (
              <span key={line} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ opacity: 0, y: "110%" }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.2, delay: 0.4 + i * 0.22, ease: [0.16, 1, 0.3, 1] }}
                >
                  {i === 1 ? <span className="gold-text italic">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1 }}
            className="mt-8 max-w-2xl font-body text-base leading-relaxed text-[var(--cx-white)]/80 md:text-lg"
          >
            Dalla casa perfetta all'auto dei tuoi sogni. Ci occupiamo noi della ricerca,
            della selezione, della trattativa e di ogni dettaglio, accompagnandoti fino al risultato.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.2 }} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              href="#missione" data-testid="hero-cta"
              className="group inline-flex items-center justify-center gap-4 bg-[var(--cx-gold)] px-9 py-4 font-body text-sm tracking-wide text-black transition-all duration-300 hover:bg-[var(--cx-gold-bright)]"
            >
              Scopri di più
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <Link
              to="/#contatti" data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-3 border border-[var(--cx-white)]/40 px-9 py-4 font-body text-sm tracking-wide text-[var(--cx-white)] transition-all duration-300 hover:border-[var(--cx-gold)] hover:text-[var(--cx-gold)]"
            >
              Contattaci
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.4 }}
            className="mt-10 flex flex-wrap gap-2.5 md:gap-3"
            data-testid="hero-badges"
          >
            {[
              { icon: Home, label: "Immobili" },
              { icon: Car, label: "Auto" },
              { icon: Handshake, label: "Consulenze" },
              { icon: Star, label: "Assistenza completa" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 border border-[var(--cx-border)] bg-black/25 px-4 py-2 backdrop-blur-sm">
                <Icon size={15} strokeWidth={1.4} className="text-[var(--cx-gold)]" />
                <span className="font-body text-xs tracking-wide text-[var(--cx-white)]/85">{label}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div style={{ opacity: fade }} className="absolute bottom-10 left-1/2 z-10 -translate-x-1/2 text-[var(--cx-gold)] cx-glow">
        <ArrowDown size={22} />
      </motion.div>
    </section>
  );
};

const Missione = () => (
  <section id="missione" data-testid="missione-section" className="py-24 md:py-36">
    <div className="cx-container">
      <Reveal><p className="overline">La nostra missione</p></Reveal>
      <Reveal delay={0.1}>
        <p className="mt-8 max-w-5xl font-display text-3xl font-light leading-tight text-[var(--cx-white)] md:text-5xl">
          Togliamo alle persone il peso di cercare, confrontare, trattare e organizzare.
          Non mettiamo semplicemente in contatto domanda e offerta:{" "}
          <span className="gold-text italic">prendiamo in carico il tuo progetto</span> e ti accompagniamo fino al risultato.
        </p>
      </Reveal>
      <div className="mt-16 gold-rule opacity-40" />
      <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
        {VALUES.map((v, i) => (
          <Reveal key={v} delay={i * 0.04}>
            <span className="font-body text-sm tracking-wide text-[var(--cx-muted)]">{v}</span>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PercheNasce = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="perche-connexa" data-testid="perche-section" className="relative py-24 md:py-36">
      <div className="cx-container grid items-center gap-14 lg:grid-cols-2 lg:gap-24">
        <Reveal>
          <div ref={ref} className="relative aspect-[4/3] overflow-hidden">
            <motion.img
              style={{ y, scale: 1.12 }}
              src={IMAGES.storyEmotional} alt="La storia di Connexa"
              className="absolute inset-0 h-full w-full object-cover img-cinematic"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>
        </Reveal>
        <div>
          <Reveal><p className="overline">Perché nasce Connexa?</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-4xl font-light leading-tight text-[var(--cx-white)] md:text-5xl">
              È nata da una domanda semplice: <span className="gold-text italic">e se qualcuno si occupasse davvero di tutto?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-8 space-y-5 font-body text-lg leading-relaxed text-[var(--cx-white)]/75">
              <p>
                Abbiamo visto troppe persone perdere tempo, energie e serenità dietro ricerche infinite,
                telefonate senza risposta e trattative estenuanti. Comprare una casa o un'auto dovrebbe
                essere un momento di gioia, non di ansia.
              </p>
              <p>
                Così è nata Connexa: un modo diverso, umano ed elegante di vivere i grandi desideri.
                Non intermediari, ma persone che prendono in carico il tuo progetto e non ti lasciano mai
                da solo — dalla prima idea fino a molto tempo dopo la firma.
              </p>
              <p className="font-display text-2xl italic text-[var(--cx-gold)]">
                Perché le persone scelgono le persone.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Showcase = ({ id, testid, kicker, title, desc, services, img, to, reverse }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section id={id} data-testid={testid} className="py-16 md:py-28">
      <div className={`cx-container grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
        <Reveal>
          <div ref={ref} className="relative aspect-[3/4] overflow-hidden">
            <motion.img
              style={{ y, scale: 1.14 }}
              src={img} alt={title}
              className="absolute inset-0 h-full w-full object-cover img-cinematic"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>
        </Reveal>
        <div>
          <Reveal><p className="overline">{kicker}</p></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-5xl font-light leading-none text-[var(--cx-white)] md:text-7xl">{title}</h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 max-w-lg font-body text-lg leading-relaxed text-[var(--cx-white)]/75">{desc}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-x-8">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={0.05 + i * 0.05}>
                <div className="border-b border-[var(--cx-border)] py-3">
                  <span className="font-body text-sm tracking-wide text-[var(--cx-white)]/85">{s.title}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to={to} data-testid={`${testid}-link`}
              className="group mt-10 inline-flex items-center gap-4 border border-[var(--cx-gold)] px-8 py-3.5 font-body text-sm tracking-wide text-[var(--cx-gold)] transition-all duration-300 hover:bg-[var(--cx-gold)] hover:text-black"
            >
              Scopri {kicker}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ComeLavoriamo = () => (
  <section id="come-lavoriamo" data-testid="come-lavoriamo-section" className="py-24 md:py-36">
    <div className="cx-container">
      <Reveal>
        <p className="overline">Come lavoriamo</p>
        <h2 className="mt-4 max-w-4xl font-display text-4xl font-light leading-tight text-[var(--cx-white)] md:text-6xl">
          Non mettiamo in contatto. <span className="gold-text italic">Prendiamo in carico.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-8 max-w-3xl font-body text-lg leading-relaxed text-[var(--cx-white)]/75">
          Non mettiamo semplicemente in contatto domanda e offerta. Prendiamo in carico
          l'intero progetto e lavoriamo al tuo posto: tu ci racconti il tuo obiettivo,
          noi troviamo le migliori opportunità, trattiamo, organizziamo e ti accompagniamo
          fino al risultato.
        </p>
      </Reveal>
      <div className="mt-16 border-t border-[var(--cx-border)]">
        {PROCESS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.05}>
            <div className="group grid grid-cols-1 items-baseline gap-4 border-b border-[var(--cx-border)] py-8 transition-colors duration-500 hover:bg-[var(--cx-surface)] md:grid-cols-12 md:gap-8 md:px-4">
              <span className="font-display text-5xl font-light text-[var(--cx-gold)]/40 transition-colors duration-500 group-hover:text-[var(--cx-gold)] md:col-span-2">{s.n}</span>
              <h3 className="font-display text-2xl font-light text-[var(--cx-white)] md:col-span-4 md:text-3xl">{s.title}</h3>
              <p className="font-body text-base text-[var(--cx-muted)] md:col-span-6">{s.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ChiSiamo = () => (
  <section id="chi-siamo" data-testid="chi-siamo-section" className="py-24 md:py-36">
    <div className="cx-container grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
      <Reveal>
        <div className="relative overflow-hidden">
          <img src={ASSETS.founders} alt="Benedetta Risso ed Eugenio Sannino" className="w-full object-cover img-cinematic" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        </div>
      </Reveal>
      <div>
        <Reveal><p className="overline">Chi siamo</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-display text-4xl font-light leading-none text-[var(--cx-white)] md:text-6xl">
            Le persone dietro <span className="gold-text italic">Connexa</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 space-y-5 font-body text-lg leading-relaxed text-[var(--cx-white)]/75">
            <p>
              Connexa nasce dall'incontro di due persone e di una convinzione condivisa:
              accompagnare davvero, non semplicemente vendere. Siamo una coppia, nella vita
              e nel lavoro, e mettiamo in ogni progetto la stessa attenzione che dedicheremmo
              a una scelta nostra.
            </p>
            <p>
              Dietro ogni casa, ogni automobile e ogni progetto ci sono persone, con i loro
              sogni e i loro tempi. Per questo seguiamo ogni cliente con ascolto, disponibilità
              e cura, dal primo incontro fino a molto tempo dopo.
            </p>
            <p className="font-display text-2xl italic text-[var(--cx-gold)]">
              Le persone scelgono le persone.
            </p>
          </div>
        </Reveal>
        <div className="mt-12 grid gap-8 sm:grid-cols-2">
          <Reveal delay={0.2}>
            <div className="border-l border-[var(--cx-gold)]/40 pl-6">
              <p className="font-display text-2xl text-[var(--cx-white)]">Benedetta Risso</p>
              <p className="mt-1 font-body text-sm tracking-wide text-[var(--cx-gold)]">Founder — Connexa Home</p>
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="border-l border-[var(--cx-gold)]/40 pl-6">
              <p className="font-display text-2xl text-[var(--cx-white)]">Eugenio Sannino</p>
              <p className="mt-1 font-body text-sm tracking-wide text-[var(--cx-gold)]">Founder — Connexa Auto</p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default function Landing() {
  return (
    <>
      <Hero />
      <Missione />
      <PercheNasce />
      <Marquee />
      <ConnexaHomeSection />
      <ConnexaAutoSection />
      <ComeLavoriamo />
      <Recensioni />
      <ChiSiamo />
      <Contatti />
    </>
  );
}
